/**
 * Frontend API types for CSR Architecture
 * Contains only types needed by the frontend without worker dependencies
 */

// Base API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    message: string;
    code?: string;
    details?: unknown;
  };
}

export type ControllerResponse<T = unknown> = ApiResponse<T>;

// User Types
export interface AuthUser {
  id: number;
  email: string;
  name?: string;
  avatar?: string;
}

export interface User extends AuthUser {
  createdAt: Date;
  updatedAt: Date;
}

// App Types
export interface App {
  id: string;
  title: string;
  description?: string;
  userId: number;
  isPublic: boolean;
  visibility: 'private' | 'public' | 'team' | 'board';
  createdAt: Date;
  updatedAt: Date;
}

export interface AppWithFavoriteStatus extends App {
  isFavorite: boolean;
  starCount: number;
  // Legacy properties for backward compatibility
  framework?: string;
  screenshotUrl?: string;
  githubRepositoryUrl?: string;
  githubRepositoryVisibility?: string;
  updatedAtFormatted?: string;
}

export interface AppWithUserAndStats extends App {
  user: {
    id: number;
    name?: string;
    avatar?: string;
  };
  starCount: number;
  viewCount: number;
  forkCount?: number;
  userStarred?: boolean;
  // Legacy properties for backward compatibility
  userName?: string;
  userAvatar?: string;
  screenshotUrl?: string;
  githubRepositoryUrl?: string;
  githubRepositoryVisibility?: string;
  updatedAtFormatted?: string;
}

// Pagination
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// File Types
export interface GeneratedCodeFile {
  path: string;
  content: string;
  language?: string;
}

export interface FileType {
  filePath: string;
  fileContents: string;
  explanation?: string;
  isGenerating?: boolean;
  needsFixing?: boolean;
  hasErrors?: boolean;
  language?: string;
}

// Blueprint Types
export interface BlueprintType {
  title: string;
  description: string;
  features?: string[];
  techStack?: string[];
  architecture?: { dataFlow?: string } | string;
  fileStructure?: Record<string, unknown>;
  // Legacy/extended fields used by UI components
  colorPalette?: string[];
  frameworks?: string[] | Array<{ name: string; version?: string; reason?: string }>;
  views?: Array<{ name: string; description?: string }>;
  userFlow?: { uiLayout?: string; uiDesign?: string; userJourney?: string };
  dataFlow?: string;
  implementationRoadmap?: Array<{ phase?: string; name?: string; description?: string; files?: Array<{ path: string; purpose?: string }> }>;
  initialPhase?: { name: string; description?: string; files?: Array<{ path: string; purpose?: string }> };
  pitfalls?: string[];
}

// WebSocket Types (for compatibility)
export interface WebSocketMessage {
  type: string;
  data?: unknown;
  // Additional properties for message handling
  message?: string;
  error?: string;
  conversationId?: string;
  tool?: string;
  isStreaming?: boolean;
  output?: string;
  outputType?: string;
  timestamp?: Date | string;
  level?: string;
  source?: string;
  // Legacy optional fields used by previous Worker-based flow
  state?: unknown;
  file?: { filePath?: string; fileContents?: string };
  filePath?: string;
  totalFiles?: number;
  previewURL?: string;
  tunnelURL?: string;
  review?: unknown;
  errors?: unknown[];
  count?: number;
  staticAnalysis?: unknown;
  runtimeErrors?: unknown[];
  clientErrors?: unknown[];
  phase?: string;
  deploymentUrl?: string;
}

export interface WebSocketMessageData {
  [key: string]: unknown;
}

// Code Fix Types
export interface CodeFixEdits {
  type: 'edit';
  filePath: string;
  search: string;
  replacement: string;
}

// Image Attachment Types
export interface ImageAttachment {
  id?: string;
  url: string;
  alt?: string;
  width?: number;
  height?: number;
  filename?: string;
  mimeType?: string;
  base64Data?: string;
}

// Error Types
export interface RateLimitError {
  message: string;
  retryAfterSeconds?: number;
}

export class RateLimitExceededError extends Error {
  constructor(public details: unknown) {
    super('Rate limit exceeded');
    this.name = 'RateLimitExceededError';
  }
  static fromRateLimitError(err: RateLimitError): RateLimitExceededError {
    return new RateLimitExceededError(err);
  }
}

export class SecurityError extends Error {
  constructor(message: string, public type: string) {
    super(message);
    this.name = 'SecurityError';
  }
}

export enum SecurityErrorType {
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  INVALID_TOKEN = 'INVALID_TOKEN',
  CSRF_VIOLATION = 'CSRF_VIOLATION',
  RATE_LIMITED = 'RATE_LIMITED'
}

// Platform Status Types
export interface PlatformStatusData {
  globalUserMessage: string;
  changeLogs: string;
  hasActiveMessage: boolean;
}

// Session Types
export interface SessionResponse {
  user: AuthUser;
  sessionId: string;
  expiresAt: Date;
}

// OAuth Provider Types
export type OAuthProvider = 'google' | 'github';

// Analytics Types
export interface UserAnalyticsResponseData {
  totalCost: number;
  totalTokens: number;
  totalRequests: number;
  costByModel: Record<string, number>;
  tokensByModel: Record<string, number>;
  requestsByModel: Record<string, number>;
  // Legacy properties
  tokensIn?: number;
  tokensOut?: number;
  lastRequestAt?: Date;
}

export interface AgentAnalyticsResponseData {
  totalCost: number;
  totalTokens: number;
  totalRequests: number;
  costByAgent: Record<string, number>;
  tokensByAgent: Record<string, number>;
  requestsByAgent: Record<string, number>;
  // Legacy properties
  tokensIn?: number;
  tokensOut?: number;
  lastRequestAt?: Date;
}

// User Stats Types
export interface UserStatsData {
  totalApps: number;
  publicApps: number;
  totalStars: number;
  totalViews: number;
}

export interface UserActivityData {
  recentApps: App[];
  favoriteApps: AppWithFavoriteStatus[];
}

// App Data Types
export interface AppsListData {
  apps: AppWithFavoriteStatus[];
  pagination: PaginationInfo;
}

export interface PublicAppsData {
  apps: AppWithUserAndStats[];
  pagination: PaginationInfo;
}

// Minimal agent summary shape used by legacy components during CSR migration
export interface AgentSummary {
  generatedCode?: Array<{ filePath: string; fileContents?: string; filePurpose?: string }>;
  query?: string;
}

export interface SingleAppData {
  app: AppWithFavoriteStatus;
}

export interface AppDetailsData {
  app: AppWithUserAndStats;
  files: GeneratedCodeFile[];
  isStarred: boolean;
  // Flattened fields for legacy components during CSR migration (optional)
  id?: string;
  title?: string;
  description?: string;
  userId?: number;
  createdAt?: Date;
  updatedAt?: Date;
  visibility?: 'private' | 'public' | 'team' | 'board';
  user?: { id: number; name: string; avatar?: string; displayName?: string };
  starCount?: number;
  viewCount?: number;
  githubRepositoryUrl?: string;
  githubRepositoryVisibility?: string;
  cloudflareUrl?: string;
  previewUrl?: string;
  agentSummary?: AgentSummary | null;
  originalPrompt?: string;
  userFavorited?: boolean;
  userStarred?: boolean;
}

export interface FavoriteToggleData {
  isFavorite: boolean;
  starCount: number;
}

export interface CreateAppData {
  app: App;
}

export interface UpdateAppVisibilityData {
  app: App;
}

export interface AppDeleteData {
  success: boolean;
}

export interface AppStarToggleData {
  isStarred: boolean;
  starCount: number;
}

// User Data Types
export interface UserAppsData {
  apps: AppWithFavoriteStatus[];
  pagination: PaginationInfo;
}

export interface ProfileUpdateData {
  user: AuthUser;
}

// Agent/CodeGen Types
export interface AgentConnectionData {
  websocketUrl: string;
  agentId: string;
}

export interface CodeGenArgs {
  query: string;
  agentMode?: 'deterministic' | 'smart';
  images?: ImageAttachment[];
}

export interface AgentPreviewResponse {
  websocketUrl: string;
  agentId: string;
  template?: {
    files: GeneratedCodeFile[];
  };
  // Legacy fields from Worker-based flow (optional)
  previewURL?: string;
  tunnelURL?: string;
}

// Rate Limit Types
export interface RateLimitError {
  message: string;
  retryAfter?: number;
  limit?: number;
  remaining?: number;
  period?: number;
  suggestions?: string[];
  limitType?: string;
}

export interface RateLimitErrorResponse {
  error: RateLimitError;
  success: false;
}

// Sorting and Filtering Types
export type AppSortOption = 'created' | 'updated' | 'stars' | 'views' | 'title' | 'recent' | 'popular' | 'trending' | 'starred';
export type SortOrder = 'asc' | 'desc';
export type TimePeriod = 'day' | 'week' | 'month' | 'year' | 'all';

export interface AppQueryOptions {
  page?: number;
  limit?: number;
  sort?: AppSortOption;
  order?: SortOrder;
  search?: string;
  period?: TimePeriod;
}

export interface PublicAppQueryOptions extends AppQueryOptions {
  featured?: boolean;
}

// Enhanced App Data
export interface EnhancedAppData extends App {
  user: {
    id: number;
    name?: string;
    avatar?: string;
  };
  stats: {
    starCount: number;
    viewCount: number;
    forkCount?: number;
  };
  metadata?: {
    tags?: string[];
    category?: string;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
  };
  // Legacy properties for backward compatibility
  starCount?: number;
  viewCount?: number;
  forkCount?: number;
  userStarred?: boolean;
}

// Model Config Types (simplified)
export interface ModelConfig {
  id: string;
  modelName: string;
  name?: string; // Legacy property for backward compatibility
  maxTokens?: number;
  max_tokens?: number; // Legacy property for backward compatibility
  temperature?: number;
  provider: string;
  reasoning_effort?: string; // Legacy property for backward compatibility
  fallbackModel?: string;
  isUserOverride?: boolean;
}

export interface ModelConfigUpdate {
  modelName?: string | null;
  maxTokens?: number | null;
  temperature?: number | null;
  reasoningEffort?: string | null;
  fallbackModel?: string | null;
  isUserOverride?: boolean;
}

// Model Configs Data Type
export interface ModelConfigsData {
  configs: Record<string, UserModelConfigWithMetadata>;
  defaults?: Record<string, ModelConfig>;
}

// Image Upload Constants and Types
export const SUPPORTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp'
] as const;

export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
export const MAX_IMAGES_PER_MESSAGE = 5;

export function isSupportedImageType(mimeType: string): boolean {
  return SUPPORTED_IMAGE_MIME_TYPES.includes(mimeType as any);
}

// Secret Management Types
export interface SecretTemplate {
  id: string;
  name: string;
  description?: string;
  fields: Array<{
    key: string;
    label: string;
    type: 'text' | 'password' | 'url';
    required: boolean;
    placeholder?: string;
    description?: string;
  }>;
  provider?: string;
  category?: string;
  // Additional UI properties
  icon?: string;
  displayName?: string;
  envVarName?: string;
  placeholder?: string;
  instructions?: string;
  required?: boolean;
  validation?: string;
}

export interface EncryptedSecret {
  id: string;
  templateId: string;
  userId: number;
  name: string;
  encryptedData: string;
  createdAt: Date;
  updatedAt: Date;
  // Additional properties for UI
  secretType?: string;
  isActive?: boolean;
  provider?: string;
  keyPreview?: string;
}

export interface SecretTemplatesData {
  templates: SecretTemplate[];
}

// Session Management Types
export interface ActiveSession {
  id: string;
  userId: number;
  deviceInfo?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: Date;
  lastActiveAt: Date;
  isCurrent: boolean;
}

export interface ActiveSessionsData {
  sessions: ActiveSession[];
}

// User Stats Extended Types
export interface UserStats {
  totalApps: number;
  publicApps: number;
  totalStars: number;
  totalViews: number;
  // Legacy properties for backward compatibility
  appCount?: number;
  publicAppCount?: number;
  totalViewsReceived?: number;
  totalLikesReceived?: number;
  achievements?: Array<{
    id: string;
    title: string;
    description: string;
    unlockedAt: Date;
    icon?: string;
  }>;
}

// User Activity Types
export interface UserActivity {
  id: string;
  type: 'app_created' | 'app_updated' | 'app_starred';
  title: string;
  description?: string;
  createdAt: Date;
  appId?: string;
  appTitle?: string;
  timestamp?: Date;
}

// GitHub Export Types
export interface GitHubExportOptions {
  repositoryName: string;
  description?: string;
  isPrivate: boolean;
  includeReadme: boolean;
}

export interface GitHubExportResult {
  success: boolean;
  repositoryUrl?: string;
  error?: string;
}

// Additional Types for CSR Compatibility
export interface UserModelConfigWithMetadata extends ModelConfig {
  id: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface AIModels {
  [provider: string]: string[];
}

export interface ByokProvidersData {
  platformModels: string[];
  providers: Array<{
    name: string;
    models: string[];
  }>;
  modelsByProvider?: Record<string, string[]>; // Legacy property
}

// Additional API type exports for compatibility
export interface ModelConfigData {
  config: ModelConfig;
}

export interface ModelConfigUpdateData {
  config: ModelConfig;
}

export interface ModelConfigTestData {
  success: boolean;
  error?: string;
  modelUsed?: string;
  latencyMs?: number;
  testResult?: { success: boolean; modelUsed?: string; latencyMs?: number; error?: string };
}

export interface ModelConfigResetData {
  config: ModelConfig;
  resetCount?: number;
}

export interface ModelConfigDefaultsData {
  defaults: Record<string, ModelConfig>;
}

export interface ModelConfigDeleteData {
  success: boolean;
}

export interface ModelProvidersListData {
  providers: string[];
}

export interface ModelProviderCreateData {
  provider: string;
}

// Provider management request/response types (compatibility for CSR)
export interface ModelProviderUpdateData { provider: string }
export interface ModelProviderDeleteData { success: boolean }
export interface ModelProviderTestData { success: boolean; message?: string }

export interface CreateProviderRequest { provider: string; apiKey?: string; config?: Record<string, unknown> }
export interface UpdateProviderRequest { provider?: string; apiKey?: string; config?: Record<string, unknown> }
export interface TestProviderRequest { provider: string }

// Secrets management
export interface SecretsData { secrets: EncryptedSecret[] }
export interface SecretStoreData { secret: EncryptedSecret; message?: string }
export interface SecretDeleteData { success: boolean; message?: string }

// Streaming response placeholder for legacy API
export interface AgentStreamingResponse { success: boolean; message?: string; stream?: unknown }

// Auth/API Keys
export interface ApiKeysData { keys: Array<{ id: string; name: string; createdAt: string }> }
export interface LoginResponseData { success: boolean; user: AuthUser; token: string }
export interface RegisterResponseData { success: boolean; user: AuthUser; token: string }
export interface ProfileResponseData { user: AuthUser }
export interface AuthProvidersResponseData { providers: OAuthProvider[] }
export interface CsrfTokenResponseData { token: string; expiresIn?: number }
